import type { Store as StoreService } from '@warp-drive/core';
import { executeSearchRequest } from 'tio-data/requests/search';
import type { OpenSearchResponse } from 'tio-data/data/resources/search.ext';
import type { BoolClause } from './open-search-builder';
import { OpenSearchQueryBuilder } from './open-search-builder';
import { OpenSearchResponseBuilder } from './open-search-response-builder';

export interface RepaymentApplicationInfo {
  applicationId: string;
  refundRequestedAmount?: number;
  refundPaidBackAmount?: number;
  refundForgivenAmount?: number;
  refundCancelledAmount?: number;
  outstandingBalance: number;
}

export interface RepaymentCheckResult {
  hasAppsInRepayment: boolean;
  applications: RepaymentApplicationInfo[];
}

/**
 * Check if an employee has any TAS applications with active repayment requests
 * using OpenSearch instead of loading all applications via Ember Data
 *
 * An application is considered "in repayment" if it has an outstanding refund balance:
 * outstandingBalance = refundRequested - (refundPaidBack + refundForgiven + refundCancelled)
 *
 * Only applications with outstandingBalance > 0 are returned.
 *
 * **PAGINATION LIMIT:** This function returns a maximum of 100 applications per employee.
 * If an employee has more than 100 applications in the system, only the first 100 will be
 * checked for repayment status. If the result count reaches 100, a console warning will be
 * logged to alert developers to increase the pagination limit.
 *
 * @param store - Ember Data store service
 * @param employeeId - The employee ID to check
 * @param companyId - The company ID (required for OpenSearch)
 * @returns Object containing repayment status and list of affected applications (max 100)
 *
 * @example
 * ```typescript
 * const result = await checkEmployeeRepaymentStatus(this.store, '12345', '67890');
 * if (result.hasAppsInRepayment) {
 *   console.log(`Found ${result.applications.length} apps in repayment`);
 *   result.applications.forEach(app => {
 *     console.log(`Application ${app.applicationId}: $${app.outstandingBalance / 100} owed`);
 *   });
 * }
 * ```
 */
export async function checkEmployeeRepaymentStatus(
  store: StoreService,
  employeeId: string | number,
  companyId: string | number
): Promise<RepaymentCheckResult> {
  if (!employeeId || !companyId) {
    return { hasAppsInRepayment: false, applications: [] };
  }

  // Convert to string for consistent handling
  const stringEmployeeId = String(employeeId);
  const stringCompanyId = String(companyId);

  // Build OpenSearch query - CRITICAL: Use snake_case for OpenSearch fields
  const PAGINATION_LIMIT = 100;
  const builder = new OpenSearchQueryBuilder()
    .setPagination(1, PAGINATION_LIMIT)
    .mustTerm('employee_id', stringEmployeeId) // Filter by specific employee
    .mustTerm('company_id', stringCompanyId); // Filter by company (required)

  // Add repayment status filters
  // An application is "in repayment" if it has refund_requested > 0
  // We'll calculate outstanding balance after fetching to determine true repayment status

  const shouldClauses: BoolClause[] = [
    {
      bool: {
        must: [
          { exists: { field: 'payment_refund_requested_amount' } },
          { range: { payment_refund_requested_amount: { gt: 0 } } },
        ],
      },
    },
    {
      bool: {
        must: [
          { exists: { field: 'payment_refund_paid_back_amount' } },
          { range: { payment_refund_paid_back_amount: { gt: 0 } } },
        ],
      },
    },
    {
      bool: {
        must: [
          { exists: { field: 'payment_refund_forgiven_amount' } },
          { range: { payment_refund_forgiven_amount: { gt: 0 } } },
        ],
      },
    },
    {
      bool: {
        must: [
          { exists: { field: 'payment_refund_cancelled_amount' } },
          { range: { payment_refund_cancelled_amount: { gt: 0 } } },
        ],
      },
    },
  ];

  // Add the should clauses with minimum_should_match
  shouldClauses.forEach((clause) => {
    builder.addShould(clause);
  });
  builder.setMinimumShouldMatch(1); // At least one must match

  // Only request fields we need — snake_case paths for OpenSearch _source
  builder.selectSourceFields(
    [
      'tas_application_id_keyword',
      'employee_id',
      'payment_refund_requested_amount',
      'payment_refund_paid_back_amount',
      'payment_refund_forgiven_amount',
      'payment_refund_cancelled_amount',
    ],
    ['id'] // Include essential ID field
  );

  const query = {
    'model-type': 'TasProgramInstance', // Note: Search against TasProgramInstance, not TasApplication
    'search-body': builder.build(),
  };

  // Execute the search via the shared request builder; unwrap `.content` for the
  // raw OpenSearch body. The demo handler intercepts the POST builder request.
  const { content } = await store.request<OpenSearchResponse>(executeSearchRequest(query));

  // Parse response using OpenSearchResponseBuilder
  const responseBuilder = OpenSearchResponseBuilder.from(content);
  const documents = responseBuilder.getDocuments();

  // Transform to our result format and calculate outstanding balance
  const applications: RepaymentApplicationInfo[] = documents
    .map((doc: Record<string, unknown>) => {
      // Extract amounts with field name normalization (camelCase / snake_case)
      const refundRequested =
        (doc.paymentRefundRequestedAmount as number | undefined) ||
        (doc.payment_refund_requested_amount as number | undefined) ||
        0;
      const refundPaidBack =
        (doc.paymentRefundPaidBackAmount as number | undefined) ||
        (doc.payment_refund_paid_back_amount as number | undefined) ||
        0;
      const refundForgiven =
        (doc.paymentRefundForgivenAmount as number | undefined) ||
        (doc.payment_refund_forgiven_amount as number | undefined) ||
        0;
      const refundCancelled =
        (doc.paymentRefundCancelledAmount as number | undefined) ||
        (doc.payment_refund_cancelled_amount as number | undefined) ||
        0;

      // Calculate outstanding balance (same logic as tas-application model)
      const outstandingBalance =
        refundRequested - (refundPaidBack + refundForgiven + refundCancelled);

      return {
        applicationId:
          (doc.tasApplicationIdKeyword as string | undefined) ||
          (doc.tas_application_id_keyword as string | undefined) ||
          (doc['tas-application-id-keyword'] as string | undefined) ||
          '',
        refundRequestedAmount: refundRequested || undefined,
        refundPaidBackAmount: refundPaidBack || undefined,
        refundForgivenAmount: refundForgiven || undefined,
        refundCancelledAmount: refundCancelled || undefined,
        outstandingBalance,
      };
    })
    .filter((app) => app.outstandingBalance > 0); // Only include apps with actual outstanding balance

  // Warn if we hit the pagination limit - indicates data may be incomplete
  if (documents.length >= 100) {
    console.warn(
      `[tas-application-repayment-check] Employee ${stringEmployeeId} has 100+ applications. ` +
        `Repayment check is incomplete. Found ${applications.length} apps in repayment ` +
        `(may be more beyond the 100 application limit). Consider increasing PAGINATION_LIMIT.`
    );
  }

  return {
    hasAppsInRepayment: applications.length > 0,
    applications,
  };
}
